import { PauseRounded, PlayArrowRounded, PortableWifiOff } from '@mui/icons-material';
import { CircularProgress, IconButton, Slider, Stack, Typography } from '@mui/material';
import { MapTransportBroadcastEventList } from 'app/pages/Map';
import { useAsyncAction } from 'app/services';
import { transportBroadcastRecordLoad } from 'app/store/map';
import HJPlayer from 'hjplayer';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

const MapTransportBroadcast = function () {
  const { transportBroadcastSelectedItem, transportSelectedDate } = useSelector((state) => state.map);
  const { terid, events } = useSelector((state) => state.map.transportSelectedItem.broadcast);
  const broadcastRef = useRef(null);
  const broadcastPlayer = useRef(null);
  const intervalCurrentPosition = useRef(null);
  const [duration, setDuration] = useState(
    moment(transportSelectedDate[1]).isSameOrAfter(moment().endOf('day'), 'day') ? moment().diff(moment().startOf('day'), 'seconds') : 86399
  );
  const [marks, setMarks] = useState([]);
  const [position, setPosition] = useState(0);
  const [currentPosition, setCurrentPosition] = useState(
    moment(transportSelectedDate[1]).isSameOrAfter(moment().endOf('day'), 'day') ? moment().diff(moment().startOf('day'), 'seconds') : 0
  );
  const [broadcastPaused, setBroadcastPaused] = useState(false);
  const [broadcastStatus, setBroadcastStatus] = useState('idle');
  const [eventChecked, setEventChecked] = useState(-1);
  const { asyncAction: fetchRecordData } = useAsyncAction(transportBroadcastRecordLoad);

  const handleEventExpandChange = (value) => {
    setPosition(value);
    setCurrentPosition(value);
  };

  const handleSliderChange = (event, value) => {
    setPosition(value);

    clearInterval(intervalCurrentPosition.current);
    intervalCurrentPosition.current = null;
  };

  const handlePauseClick = () => {
    setBroadcastPaused(!broadcastPaused);
  };

  useEffect(() => {
    if (!HJPlayer.isSupported()) {
      return;
    }

    if (moment(transportSelectedDate[1]).isSameOrAfter(moment().endOf('day'), 'day') && currentPosition >= duration - 30) {
      broadcastPlayer.current = new HJPlayer(
        {
          type: 'flv',
          isLive: true,
          hasAudio: false,
          hasVideo: true,
          url: `${config.mapBroadcastUrl}:12060/live.flv?devid=${terid}&chl=${transportBroadcastSelectedItem}&st=1&audio=0`,
        },
        {
          enableWorker: true,
          enableStashBuffer: false,
        }
      );
    } else {
      const startTime = position !== 0 ? moment.utc((position - 120) * 1000).format('HHmmss') : '000000';
      const endTime = position !== 0 ? moment.utc((position + 120) * 1000).format('HHmmss') : '000400';
      const startDate = moment(transportSelectedDate[0]).format('YYYYMMDD');
      const endDate = moment(transportSelectedDate[1]).format('YYYYMMDD');

      broadcastPlayer.current = new HJPlayer(
        {
          type: 'm3u8',
          isLive: false,
          hasAudio: false,
          hasVideo: true,
          url: `${config.mapBroadcastUrl}:8090/play/${terid}/${transportBroadcastSelectedItem}/${startDate}${startTime}_${endDate}${endTime}_main.m3u8`,
        },
        {
          autoStartLoad: true,
          startPosition: -1,
          enableWorker: true,
          startFragPrefetch: true,
        }
      );
    }

    broadcastPlayer.current.attachMediaElement(broadcastRef.current);
    broadcastPlayer.current.load();

    broadcastPlayer.current.on(HJPlayer.Events.MEDIA_INFO, () => {
      setBroadcastStatus('fulfilled');
    });

    broadcastPlayer.current.on(HJPlayer.Events.ERROR, (reason) => {
      switch (reason) {
        case 'NETWORK_ERROR':
        case 'MEDIA_ERROR':
          setBroadcastStatus('rejected');
          break;
        default:
          setBroadcastStatus(broadcastStatus);
      }
    });

    return () => {
      broadcastPlayer.current.unload();
      broadcastPlayer.current.off();
      broadcastPlayer.current.detachMediaElement();
    };
  }, [terid, position, transportBroadcastSelectedItem]);

  useEffect(() => {
    const marks = events.map((broadcastEvent) => ({
      value: moment(broadcastEvent.time).diff(moment(broadcastEvent.time).startOf('day'), 'seconds'),
      label: broadcastEvent.type,
      alarmid: broadcastEvent.alarmid,
    }));

    setMarks(marks);
  }, [events]);

  useEffect(() => {
    broadcastPaused ? broadcastRef.current.pause() : broadcastRef.current.play();
  }, [broadcastPaused]);

  useEffect(() => {
    const markIndex = marks.findIndex((mark) => mark.value > position - 300 && mark.value < position + 300);

    setEventChecked(markIndex !== -1 ? marks[markIndex].alarmid : markIndex);
  }, [position]);

  useEffect(() => {
    if (!moment(transportSelectedDate[1]).isSameOrAfter(moment().endOf('day'), 'day')) {
      return;
    }

    const intervalDuration = setInterval(() => {
      setDuration(moment().diff(moment().startOf('day'), 'seconds'));
    }, 1000);

    intervalCurrentPosition.current = setInterval(() => {
      setCurrentPosition(moment().diff(moment().startOf('day'), 'seconds'));
    }, 1000);

    return () => {
      clearInterval(intervalDuration);
      clearInterval(intervalCurrentPosition.current);
    };
  }, []);

  useEffect(() => {
    fetchRecordData(terid);
  }, []);

  return (
    <React.Fragment>
      <Stack
        direction='column'
        alignItems='center'
        justifyContent='center'
        sx={(theme) => ({
          width: '100%',
          flex: '1 0 auto',
          padding: theme.spacing(0),
          position: 'relative',
          bgcolor: theme.palette.common.black,
          video: {
            width: '100%',
            height: '100%',
            position: 'absolute',
          },
        })}>
        <video ref={broadcastRef} autoPlay />
        <Stack
          direction='column'
          sx={(theme) => ({
            width: '100%',
            marginTop: 'auto',
            padding: theme.spacing(0, 3, 2),
            zIndex: '100',
            '.MuiSlider-thumb': {
              width: '12px',
              height: '12px',
              bgcolor: theme.palette.common.white,
              '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
                boxShadow: 'inherit',
              },
              '&:before': {
                display: 'none',
              },
            },
            '.MuiSlider-valueLabel': {
              color: theme.palette.common.black,
              backgroundColor: theme.palette.background.default,
              marginBottom: '8px !important',
              padding: '2px 6px',
              fontSize: '10px',
              '.MuiTooltip-arrow': {
                color: theme.palette.background.default,
              },
            },
            '.MuiSlider-markLabel': {
              top: '-8px',
              color: theme.palette.common.white,
              bgcolor: theme.palette.warning.dark,
              padding: '2px 6px',
              fontSize: '10px',
              borderRadius: theme.shape.borderRadius,
            },
            '.MuiSlider-mark': {
              width: '6px',
              marginLeft: '-2px',
              bgcolor: theme.palette.warning.dark,
            },
            ...(broadcastStatus === 'rejected' && {
              display: 'none',
            }),
          })}>
          <Slider
            min={0}
            marks={marks}
            max={duration}
            value={currentPosition}
            valueLabelDisplay='auto'
            valueLabelFormat={(v) => moment.utc(v * 1000).format('HH:mm:ss')}
            onChange={(_, value) => setCurrentPosition(value)}
            onChangeCommitted={handleSliderChange}
            sx={(theme) => ({
              color: theme.palette.common.white,
              marginBottom: '6px',
            })}
          />
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='space-between'
            sx={(theme) => ({
              marginTop: theme.spacing(-2),
              '.MuiTypography-root': {
                padding: '0',
                color: theme.palette.background.paper,
              },
            })}>
            <Typography>{moment.utc(position * 1000).format('HH:mm:ss')}</Typography>
            <Typography>{moment.utc(duration * 1000).format('HH:mm:ss')}</Typography>
          </Stack>
        </Stack>
        <Stack
          direction='column'
          alignItems='center'
          justifyContent='center'
          sx={(theme) => ({
            position: 'absolute',
            bottom: '55px',
            right: '10px',
            bgcolor: theme.palette.common.black,
            svg: {
              color: theme.palette.common.white,
            },
            '.MuiButtonBase-root': {
              color: theme.palette.primary.contrastText,
              svg: {
                color: theme.palette.primary.contrastText,
              },
            },
            ...(broadcastStatus === 'fulfilled' && {
              bgcolor: 'transparent',
            }),
          })}>
          {broadcastStatus === 'fulfilled' && (
            <IconButton onClick={handlePauseClick}>
              {broadcastPaused ? <PlayArrowRounded fontSize='medium' /> : <PauseRounded fontSize='medium' />}
            </IconButton>
          )}
          {broadcastStatus === 'rejected' && <PortableWifiOff fontSize='medium' />}
          {broadcastStatus === 'idle' && <CircularProgress size={24} />}
        </Stack>
      </Stack>
      <Stack
        direction='row'
        alignItems='center'
        sx={{
          width: '100%',
          height: '200px',
          flex: '0 0 auto',
          borderRadius: '0',
          bgcolor: 'background.paper',
        }}>
        {events.length !== 0 && <MapTransportBroadcastEventList checkedItem={eventChecked} onChangePlayTime={handleEventExpandChange} />}
        {events.length === 0 && (
          <Stack
            direction='column'
            alignItems='center'
            justifyContent='center'
            sx={(theme) => ({
              width: '100%',
              height: '100%',
              padding: theme.spacing(2),
            })}>
            <Typography align='center' variant='body1' component='span'>
              Здесь пока ничего нет
            </Typography>
            <Typography align='center' color='text.secondary' variant='caption' component='span'>
              На заданную дату событий не найдено
            </Typography>
          </Stack>
        )}
      </Stack>
    </React.Fragment>
  );
};

export default MapTransportBroadcast;
